import React, { useEffect, useState } from "react";
import {
  Route,
  Switch,
  Redirect,
  useLocation,
  useHistory,
} from "react-router-dom";
import { makeStyles } from "@material-ui/styles";
import { loader } from "@monaco-editor/react";
import { Button, AppBar, Toolbar } from "@material-ui/core";
import NavLink, {
  usePushHistory,
  useReplaceHistory,
} from "./components/NavLink";

import WorkflowSearch from "./pages/executions/WorkflowSearch";
import TaskSearch from "./pages/executions/TaskSearch";

import Execution from "./pages/execution/Execution";
import WorkflowDefinitions from "./pages/definitions/Workflow";
import WorkflowDefinition from "./pages/definition/WorkflowDefinition";
import TaskDefinitions from "./pages/definitions/Task";
import TaskDefinition from "./pages/definition/TaskDefinition";
import EventHandlerDefinitions from "./pages/definitions/EventHandler";
import EventHandlerDefinition from "./pages/definition/EventHandler";
import TaskQueue from "./pages/misc/TaskQueue";
import KitchenSink from "./pages/kitchensink/KitchenSink";
import DiagramTest from "./pages/kitchensink/DiagramTest";
import Examples from "./pages/kitchensink/Examples";
import Gantt from "./pages/kitchensink/Gantt";

import CustomRoutes from "./plugins/CustomRoutes";
import Workbench from "./pages/workbench/Workbench";
import OnlyExecutionPage from "./pages/execution/OnlyExecutionPage";
import DefaultLoader from "./components/DefaultLoader";
import { getParentLocation } from "./utils/helpers";
import { getTabsList } from "./data/services/API";

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: "#fafafa",
    display: "flex",
    paddingLeft: "0",
  },
  body: {
    width: "100vw",
    height: "100vh",
    paddingTop: theme.overrides.MuiAppBar.root.height,
  },
  body2: {
    width: "100vw",
    height: "100vh",
  },
  toolbarRight: {
    marginLeft: "auto",
    display: "flex",
    flexDirection: "row",
  },
  toolbarRegular: {
    minHeight: theme.overrides.MuiAppBar.root.height,
    paddingLeft: "8px",
  },
  menuBreadCrumb: {
    "& nav": {
      height: "100%",
    },
    "& ol": {
      height: "100%",
    },
  },
  menuBar: {
    height: "100%",
    width: 64,
  },
  navBtn: {
    fontSize: "14px",
    fontWeight: "600",
    fontFamily: "Poppins",
    height: "90%",
    padding: "6px 12px !important",
    "& span": {
      color: "var(--theme-primary) !important",
    },
    "&:hover": {
      "& span": {
        color: "#e0f4fc !important",
      },
      backgroundColor: "var(--theme-primary)",
    },
  },
  active: {
    backgroundColor: "var(--theme-primary)",
    "& span": {
      color: "#e0f4fc !important",
    },
  },
  notAuthenticated: {
    width: "100vw",
    height: "100vh",
    paddingTop: theme.overrides.MuiAppBar.root.height,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
}));

export const WorkflowErrors = React.createContext();

export const WorkflowErrorsProvider = ({ children }) => {
  const [workflowErrors, setWorkflowErrors] = useState([]);
  return (
    <WorkflowErrors.Provider value={[workflowErrors, setWorkflowErrors]}>
      {children}
    </WorkflowErrors.Provider>
  );
};

// export const MenuContext = React.createContext();

// export const MenuProvider = ({ children }) => {
//   const [activeNav, setActiveNav] = useState("execution");
//   return (
//     <MenuContext.Provider value={{ activeNav, setActiveNav }}>
//       {children}
//     </MenuContext.Provider>
//   );
// };

export default function App({ appData, flowRedirectUrl }) {
  const classes = useStyles();
  const location = useLocation();
  const navigate = usePushHistory();
  const replacePage = useReplaceHistory();

  const [activeNav, setActiveNav] = useState("execution");
  const [requestID, setRequestID] = useState(null);
  const [loader, setLoader] = useState({ isLoading: false, view: "" });
  const [tabsList, setTabsList] = useState(null);
  const [flowComplete, setFlowComplete] = useState(false);

  const history = useHistory();
  useEffect(() => {
    const segments = location.pathname
      .split("/")
      .filter((segment) => segment !== ""); // Remove empty segments

    switch (location.pathname) {
      case "/":
      case "/search":
      case "/search/tasks":
      case "/execution/" + segments[1]:
        setActiveNav("ex");
        break;
      case "/workflowDef":
      case "/workflowDef/" + segments[1]:
      case "/workflowDef/" + segments[1] + "/" + segments[2]:
      case "/workflowDefs":
      case "/taskDefs":
      case "/taskDef":
      case "/taskDef/" + segments[1]:
      case "/eventHandlerDef":
        setActiveNav("def");
        break;
      case "/taskQueue":
      case "/taskQueue/" + segments[1]:
        setActiveNav("tskque");
        break;
      case "/workbench":
        setActiveNav("wb");
        break;
      default:
        setActiveNav("");
        break;
    }
  }, [location]);

  function responseFunction(event) {
    let { data } = event;
    if (window.location.host.includes("localhost")) {
      setLoader({ isLoading: false, view: "FLOW_MAIN" });
      setRequestID("");
      // setRequestID(1244883);
      // navigate(`/ticket_summary/${1244883}`);
      // setLoader({ isLoading: false, view: "TICKET_SUMMARY" });
      // window.removeEventListener("message", responseFunction);
    } else {
      if (data?.unique_id) {
        if (data?.requestId) {
          setRequestID(event.data.requestId);
          navigate(`/ticket_summary/${event.data.requestId}`);
          setLoader({ isLoading: false, view: "TICKET_SUMMARY" });
          window.removeEventListener("message", responseFunction);
        } else if (data?.flowRedirectUrl) {
          setLoader({ isLoading: false, view: "FLOW_MAIN" });
          setRequestID("");
          navigate(data?.flowRedirectUrl);
          // replacePage(data?.flowRedirectUrl);
        } else {
          setLoader({ isLoading: false, view: "FLOW_MAIN" });
          setRequestID("");
        }
      }
    }
  }

  useEffect(() => {
    setLoader({ isLoading: true, view: "" });
    if (
      appData ||
      (window.location.host.includes("localhost") && !flowComplete)
    ) {
      responseFunction({ data: appData, flowRedirectUrl: flowRedirectUrl });
    } else if (window.location === window.parent.location) {
      setLoader({ isLoading: false, view: "" });
    }
  }, [appData]);

  useEffect(() => {
    if (loader.view === "FLOW_MAIN") {
      fetchTabsList();
    }
  }, [loader]);

  const fetchTabsList = async () => {
    try {
      const resp = await getTabsList();
      if (resp.status === 200) {
        const data = resp.data;

        setTabsList(data);
      }
    } catch (error) {
      setTabsList(null);
    }
  };

  const renderRoutes = () => {
    const routes = [];
    const isTabActive = (tab) =>
      tab?.active === "True" && tab?.hide === "False";

    const addRoute = (path, key, component) => {
      let permissions = null;
      if (tabsList) {
        switch (component.name) {
          case "WorkflowPanel":
            permissions = tabsList?.Execution || null;
            break;
          case "TaskSearch":
            permissions = tabsList?.Execution?.tasks || null;
            break;
          case "Execution":
            permissions = tabsList?.Execution || null;
            break;
          case "WorkflowDefinitions":
          case "Workflow":
          case "TaskDefinition":
          case "TaskDefinitions":
          case "EventHandlerDefinition":
          case "EventHandlers":
            permissions = tabsList?.Definition || null;
            break;
          case "TaskQueue":
            permissions = tabsList?.["Task Queues"] || null;
            break;
          case "Workbench":
            permissions = tabsList?.Workbench || null;
            break;
        }
      }

      routes.push(
        <Route
          exact
          path={path}
          key={key}
          render={(props) => {
            const DynamicComponent = component;
            return <DynamicComponent {...props} permissions={permissions} />;
          }}
        />
      );
    };

    if (isTabActive(tabsList?.Execution)) {
      addRoute("/search", "search", WorkflowSearch);
      addRoute("/search/tasks", "search-tasks", TaskSearch);
      addRoute("/execution/:id/:taskId?", "execution", Execution);
    }

    if (isTabActive(tabsList?.Definition)) {
      if (isTabActive(tabsList?.Definition?.workflows)) {
        addRoute("/workflowDefs", "workflowDefs", WorkflowDefinitions);
        addRoute(
          "/workflowDef/:name?/:version?",
          "workflowDef",
          WorkflowDefinition
        );
      }
      if (isTabActive(tabsList?.Definition?.tasks)) {
        addRoute("/taskDefs", "taskDefs", TaskDefinitions);
        addRoute("/taskDef/:name?", "taskDef", TaskDefinition);
      }
      if (isTabActive(tabsList?.Definition?.event_handlers)) {
        addRoute(
          "/eventHandlerDef",
          "eventHandlerDef",
          EventHandlerDefinitions
        );
        addRoute(
          "/eventHandlerDef/:name",
          "eventHandlerDefinition",
          EventHandlerDefinition
        );
      }
    }

    if (isTabActive(tabsList?.["Task Queues"])) {
      if (isTabActive(tabsList?.["Task Queues"]?.task_name_dropdown)) {
        addRoute("/taskQueue/:name?", "taskQueue", TaskQueue);
      }
    }

    if (isTabActive(tabsList?.Workbench)) {
      if (isTabActive(tabsList?.Workbench?.run_history)) {
        addRoute("/workbench", "workbench", Workbench);
      }
      if (isTabActive(tabsList?.Workbench?.workflow_workbench)) {
        addRoute("/workbench/:name?/:version?", "workbench-detail", Workbench);
      }
      // if (isTabActive(tabsList?.Workbench?.execution_history)) {
      //   addRoute("/workbench/execution/:id", "workbench-execution", ExecutionHistory);
      // }
    }

    const kitchenRoutes = [
      { path: "/kitchen", key: "kitchen", component: KitchenSink },
      {
        path: "/kitchen/diagram",
        key: "kitchen-diagram",
        component: DiagramTest,
      },
      {
        path: "/kitchen/examples",
        key: "kitchen-examples",
        component: Examples,
      },
      { path: "/kitchen/gantt", key: "kitchen-gantt", component: Gantt },
    ];

    kitchenRoutes.forEach(({ path, key, component }) =>
      addRoute(path, key, component)
    );
    routes.push(<CustomRoutes key="custom-routes" />);

    return routes;
  };

  const renderNavButtons = () => {
    return (
      <>
        {tabsList?.Execution?.active === "True" &&
          tabsList?.Execution?.hide === "False" && (
            <Button
              id="exeTab"
              className={`${classes.navBtn} ${
                activeNav === "ex" ? classes.active : ""
              }`}
              component={NavLink}
              path="/">
              Executions
            </Button>
          )}
        {tabsList?.Definition?.active === "True" &&
          tabsList?.Definition?.hide === "False" && (
            <Button
              id="defTab"
              className={`${classes.navBtn} ${
                activeNav === "def" ? classes.active : ""
              }`}
              component={NavLink}
              path="/workflowDefs">
              Definitions
            </Button>
          )}
        {tabsList?.["Task Queues"]?.active === "True" &&
          tabsList?.["Task Queues"]?.hide === "False" && (
            <Button
              id="tskqueTab"
              className={`${classes.navBtn} ${
                activeNav === "tskque" ? classes.active : ""
              }`}
              component={NavLink}
              path="/taskQueue">
              Task Queues
            </Button>
          )}
        {tabsList?.Workbench?.active === "True" &&
          tabsList?.Workbench?.hide === "False" && (
            <Button
              id="wbTab"
              className={`${classes.navBtn} ${
                activeNav === "wb" ? classes.active : ""
              }`}
              component={NavLink}
              path="/workbench">
              Workbench
            </Button>
          )}
      </>
    );
  };

  return (
    // <MenuProvider>
    <WorkflowErrorsProvider>
      {loader.isLoading ? (
        <DefaultLoader
          displayModal={loader.isLoading}
          message={
            !requestID && loader.view === "TICKET_SUMMARY"
              ? "Workflow is getting loaded"
              : !requestID && loader.view === "FLOW_MAIN"
              ? "Your app is being loaded"
              : ""
          }
        />
      ) : requestID && loader.view === "TICKET_SUMMARY" ? (
        <div className={classes.body2}>
          <Switch>
            <Redirect exact from="/" to="ticket_summary" />
            <Route path="/ticket_summary/:id" component={OnlyExecutionPage} />
          </Switch>
        </div>
      ) : requestID !== null && loader.view === "FLOW_MAIN" ? (
        <>
          <AppBar
            position="fixed"
            style={{
              justifyContent: "flex-start",
              boxShadow:
                "0 1px 2px 0 rgb(0 0 0 / 10%), 0 0 2px 0 rgb(0 0 0 / 10%)",
            }}
            id="main-header">
            <Toolbar classes={{ regular: classes.toolbarRegular }}>
              {renderNavButtons()}
            </Toolbar>
          </AppBar>
          <div className={classes.body} id="main-body">
            <Switch>
              <Redirect
                exact
                from="/"
                to={flowRedirectUrl ? flowRedirectUrl : "search"}
              />
              {renderRoutes()}
            </Switch>
          </div>
        </>
      ) : (
        <div className="custom_modal">
          <div className="custom_modal-content">
            <div className="custom_modal-flex">
              <div className="custom_modal_static-msg">
                You do not have access to this page through this link. Please
                contact your admin.
              </div>
            </div>
          </div>
        </div>
      )}
    </WorkflowErrorsProvider>
    //  </MenuProvider>
  );
}

if (process.env.REACT_APP_MONACO_EDITOR_USING_CDN === "false") {
  loader.config({ paths: { vs: "/monaco-editor/min/vs" } });
}
